// plugins/mentions.tsx

import React, { useEffect, useRef } from "react";
import { ReactEditor, useSlate } from "slate-react";
import { Transforms, Range, Editor } from "slate";
import { useMentionContext } from "./mentionProvider";
import { WorkspaceMember } from "../../../lib/types";

export interface MentionElementType {
  type: "mention";
  mentionType: "user";
  userId: string;
  userName: string;
  children: [{ text: "" }];
}

export const insertMention = (editor: ReactEditor, user: WorkspaceMember) => {
  const mention: MentionElementType = {
    type: "mention",
    userId: user.id,
    userName: user.displayName,
    mentionType: "user",
    children: [{ text: "" }],
  };

  const { selection } = editor;

  if (selection) {
    // If there is a range (like the "@ga"), delete it before inserting the mention
    const [start] = Range.edges(selection);
    const wordBefore = Editor.before(editor, start, { unit: "word" });
    const before = wordBefore && Editor.before(editor, wordBefore);
    const beforeRange = before && Editor.range(editor, before, start);

    if (beforeRange) {
      const beforeText = Editor.string(editor, beforeRange);
      const mentionMatch = beforeText.match(/^@(\w*)$/);

      if (mentionMatch) {
        // Select the range and delete it
        Transforms.select(editor, beforeRange);
        Transforms.delete(editor);
      }
    }

    // Insert the mention
    Transforms.insertNodes(editor, [mention, { text: "" }]);

    // Move the cursor after the mention
    Transforms.move(editor);
  }
};

export const MentionToolbar = () => {
  const { target, setTarget, searchTerm, mentionResults, mentionIndex, } =
    useMentionContext();
  const editor = useSlate() as ReactEditor;
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !target) return;

    const domRange = ReactEditor.toDOMRange(editor, target);
    const rect = domRange.getBoundingClientRect();
    el.style.top = `${rect.top + window.pageYOffset + 24}px`;
    el.style.left = `${rect.left + window.pageXOffset}px`;
  }, [mentionResults.length, editor, target]);

  const handleSelectMention = (user: WorkspaceMember) => {
    if (!target) return;
    Transforms.select(editor, target);
    insertMention(editor, user);
    setTarget(undefined);
  };

  return target && mentionResults.length > 0 ? (
    <div
      ref={ref}
      className="fixed z-10 shadow-elevation-1 rounded-md bg-white"
    >
      {mentionResults.map((user, i) => (
        <div
          key={user.id}
          onClick={() => handleSelectMention(user)}
          className={`cursor-pointer px-2 py-1 font-style-label-xs ${
            i === mentionIndex && " bg-gray-1 text-white last:rounded-b-md"
          }`}
        >
          {user.displayName}
        </div>
      ))}
    </div>
  ) : null;
};

export const withMentions = (editor: ReactEditor): ReactEditor => {
  const { isInline, isVoid } = editor;

  editor.isInline = (element) => (element.type === "mention" ? true : isInline(element));
  editor.isVoid = (element) => (element.type === "mention" ? true : isVoid(element));

  return editor;
};